/**
 * CotPostVenta — Extras con link propio, solicitudes de devolución (aprobación de Dirección)
 * y actividad del link de pago (aviso de pago incompleto). Copiado del Creador de Itinerarios.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Copy, Eye, Loader2, PackagePlus, Plus, Trash2, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, apiErrorMessage } from "@/lib/api";

const money = (n, cur = "EUR") => new Intl.NumberFormat("es-ES", { style: "currency", currency: cur }).format(n || 0);
const dt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const EXTRA_BADGE = {
  pending: ["Pendiente de pago", "border-amber-300 bg-amber-50 text-amber-800"],
  paid: ["Pagado", "border-emerald-300 bg-emerald-50 text-emerald-800"],
  cancelled: ["Cancelado", "border-slate-300 bg-slate-100 text-slate-600"],
  refunded: ["Devuelto", "border-slate-300 bg-slate-100 text-slate-600"],
};
const RR_BADGE = {
  pending: ["Pendiente de aprobación", "border-amber-300 bg-amber-50 text-amber-800"],
  executed: ["Devuelta", "border-emerald-300 bg-emerald-50 text-emerald-800"],
  rejected: ["Rechazada", "border-rose-300 bg-rose-50 text-rose-800"],
  failed: ["Error PayPal", "border-rose-300 bg-rose-50 text-rose-800"],
};

export function CotExtras({ cotId }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["cot-extras", cotId], queryFn: async () => (await api.get(`/cotizaciones/${cotId}/extras`)).data });
  const [form, setForm] = useState(null);
  const inv = () => qc.invalidateQueries({ queryKey: ["cot-extras", cotId] });
  const create = useMutation({
    mutationFn: async (b) => (await api.post(`/cotizaciones/${cotId}/extras`, b)).data,
    onSuccess: () => { toast.success("Extra creado · copia el link y envíaselo al cliente"); setForm(null); inv(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: async (xid) => (await api.delete(`/cotizaciones/${cotId}/extras/${xid}`)).data,
    onSuccess: () => { toast.success("Extra cancelado"); inv(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Link del extra copiado"));
  const extras = q.data?.extras || [];
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="cot-extras">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground"><PackagePlus className="h-3.5 w-3.5" />Extras post-venta</Label>
        {!form && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setForm({ title: "", description: "", amount_eur: "" })} data-testid="cot-extra-add"><Plus className="mr-1 h-3 w-3" />Añadir extra</Button>}
      </div>
      <p className="text-[11px] text-muted-foreground">Servicios añadidos tras la venta (traslados, excursiones, upgrades…). Cada extra tiene su propio link de pago y no altera el precio del viaje.</p>
      {form && (
        <div className="grid grid-cols-[1fr_1fr_110px_auto] gap-1.5" data-testid="cot-extra-form">
          <Input placeholder="Título (p. ej. Traslado privado)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-8 text-xs" data-testid="cot-extra-title" />
          <Input placeholder="Descripción (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-8 text-xs" />
          <Input type="number" step="0.01" placeholder="Importe €" value={form.amount_eur} onChange={(e) => setForm({ ...form, amount_eur: e.target.value })} className="h-8 text-xs" data-testid="cot-extra-amount" />
          <div className="flex gap-1">
            <Button size="sm" className="h-8 text-xs" disabled={!form.title || !Number(form.amount_eur) || create.isPending} onClick={() => create.mutate({ ...form, amount_eur: Number(form.amount_eur) })} data-testid="cot-extra-save">{create.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Crear"}</Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setForm(null)}><X className="h-3 w-3" /></Button>
          </div>
        </div>
      )}
      {extras.length === 0 && !form && <p className="text-xs text-muted-foreground" data-testid="cot-extras-empty">Sin extras.</p>}
      {extras.map((x) => {
        const [lbl, cls] = EXTRA_BADGE[x.status] || [x.status, ""];
        return (
          <div key={x.id} className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-2" data-testid={`cot-extra-${x.id}`}>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium">{x.title}{x.description ? <span className="font-normal text-muted-foreground"> · {x.description}</span> : null}</p>
              <p className="text-[11px] text-muted-foreground">{money(x.amount, x.currency)} <span className="opacity-70">({money(x.amount_eur)})</span>{x.paid_at ? ` · pagado ${dt(x.paid_at)}` : ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Badge variant="outline" className={`text-[10px] ${cls}`} data-testid={`cot-extra-status-${x.id}`}>{lbl}</Badge>
              {x.status === "pending" && <>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy(x.pay_url)} data-testid={`cot-extra-copy-${x.id}`}><Copy className="mr-1 h-3 w-3" />Copiar link</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => cancel.mutate(x.id)} title="Cancelar extra" data-testid={`cot-extra-cancel-${x.id}`}><Trash2 className="h-3 w-3" /></Button>
              </>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CotRefundRequests({ publicToken }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["refund-requests", publicToken], enabled: !!publicToken,
    queryFn: async () => (await api.get(`/paypal/refund-requests?token=${publicToken}`)).data });
  const [note, setNote] = useState({});
  const decide = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/paypal/refund-requests/${id}/${action}`, { note: note[id] || "" })).data,
    onSuccess: (_, v) => {
      toast.success(v.action === "approve" ? "Devolución aprobada y ejecutada en PayPal" : "Solicitud rechazada");
      qc.invalidateQueries({ queryKey: ["refund-requests", publicToken] });
      qc.invalidateQueries({ queryKey: ["paypal-status", publicToken] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const rows = q.data?.requests || [];
  if (!rows.length) return null;
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="cot-refund-requests">
      <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground"><Undo2 className="h-3.5 w-3.5" />Solicitudes de devolución</Label>
      {rows.map((r) => {
        const [lbl, cls] = RR_BADGE[r.status] || [r.status, ""];
        return (
          <div key={r.id} className="rounded-md border px-2.5 py-2 text-xs" data-testid={`refund-request-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p><b>{money(r.amount, r.currency)}</b> <span className="text-muted-foreground">({money(r.amount_eur)}) · {r.requested_by_name} · {dt(r.requested_at)}</span></p>
              <Badge variant="outline" className={`text-[10px] ${cls}`} data-testid={`refund-request-status-${r.id}`}>{lbl}</Badge>
            </div>
            {r.reason && <p className="mt-0.5 text-muted-foreground">Motivo: {r.reason}</p>}
            {r.decision_note && <p className="mt-0.5 text-muted-foreground">Dirección: {r.decision_note}</p>}
            {r.error && <p className="mt-0.5 text-rose-700">{r.error}</p>}
            {r.status === "pending" && q.data?.can_approve && (
              <div className="mt-2 flex items-center gap-1.5">
                <Input placeholder="Nota (opcional)" value={note[r.id] || ""} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} className="h-7 text-xs" data-testid={`refund-request-note-${r.id}`} />
                <Button size="sm" className="h-7 text-xs" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, action: "approve" })} data-testid={`refund-request-approve-${r.id}`}><Check className="mr-1 h-3 w-3" />Aprobar y devolver</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, action: "reject" })} data-testid={`refund-request-reject-${r.id}`}><X className="mr-1 h-3 w-3" />Rechazar</Button>
              </div>
            )}
            {r.status === "pending" && !q.data?.can_approve && <p className="mt-1 text-[11px] text-amber-700">Esperando aprobación de Dirección.</p>}
          </div>
        );
      })}
    </div>
  );
}

export function CotPayActivity({ cotId }) {
  const q = useQuery({ queryKey: ["cot-pay-activity", cotId], queryFn: async () => (await api.get(`/cotizaciones/${cotId}/pay-activity`)).data, refetchInterval: 60000 });
  const d = q.data;
  if (!d || (!d.opens && !d.cancels && !d.pending_orders)) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs" data-testid="cot-pay-activity">
      <Eye className="h-3.5 w-3.5 text-muted-foreground" />
      <span>Link abierto <b>{d.opens}</b> {d.opens === 1 ? "vez" : "veces"}{d.last_open ? ` · última ${dt(d.last_open)}` : ""}</span>
      {d.cancels > 0 && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-800" data-testid="cot-pay-activity-cancels">{d.cancels} pago{d.cancels > 1 ? "s" : ""} cancelado{d.cancels > 1 ? "s" : ""} en PayPal{d.last_cancel ? ` · ${dt(d.last_cancel)}` : ""}</Badge>}
      {d.pending_orders > 0 && <Badge variant="outline" className="text-[10px]">{d.pending_orders} intento{d.pending_orders > 1 ? "s" : ""} sin completar</Badge>}
    </div>
  );
}
